import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import CodeHighlight from './components/code-highlight';

const styles = `
  .__grade {
    font-variation-settings: "GRAD" 88;
    --owl-distance: 2rem;
  }

  .__grade__grid {
    display: grid;
    gap: 2rem;
    grid-template-columns: 100%;
  }

  @media (min-width: 50rem) {
    .__grade__grid {
      grid-template-columns: 2fr 1fr;
    }
  }
  .__grade__preview {
    font-size: 0.75rem;
    line-height: 1.25;
    background: var(--blue-brighter);
    display: grid;
    place-items: center;
    padding: 1rem;
    border-radius: 0.25rem;
  }

  .__grade__label {
    position: absolute;
    right: 0.5rem;
    max-width: calc(100% - 1rem);
  }
  .__grade__label.top {
    bottom: calc(100% - 0.75rem);
  }
  .__grade__label.top svg {
    scale: 100% -100%;
    top: calc(100% - 0.375em);
  }

  .__grade__label.bottom {
    top: calc(100% - 0.75rem);
  }
  .__grade__label.bottom svg {
    scale: 100% 100%;
    bottom: calc(100% - 0.375em);
  }

  .__grade__label div {
    background: white;
    padding: 0.25rem 0.75rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-style: italic;
    box-shadow: var(--soft-shadow-small);
    border: 0.5px solid var(--blue-brighter);
    color: var(--blue);
    background: white;
    z-index: 1;
  }

  .__grade__label svg {
    position: absolute;
    left: calc(100% - 0.25rem);
    width: 1.5rem;
    height: 1.5rem;
    fill: var(--blue);
  }

  .__grade__example {
    position: relative;
    padding: 1rem;
    background: var(--grey-brighter);
    border-radius: 0.25rem;
  }

  .__grade__example.top-space {
    padding-top: 1.5rem;
  }
  .__grade__example.bottom-space {
    padding-bottom: 1.5rem;
  }

  .__grade__controls label {
    display: block;
    font-weight: 800;
    font-size: 0.875rem;
  }

  .__grade__controls input {
    width: 100%;
  }

  .__grade__paragraph {
    font-family: Robotoo;
    font-size: 1rem;
    font-variation-settings: 'GRAD' var(--grade);
  }

  .__grade__fluent {
    margin: 0;
    font-family: Robotoo;
    font-variation-settings: 'GRAD' 0;
  }

  .__grade__cta {
    font-size: 1rem;
    font-family: Robotoo;
    background: var(--blue);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-variation-settings: 'GRAD' -50;
    border: none;
    display: inline-block;
    margin: 0 auto;
  }

  .__grade__cta:hover {
    font-variation-settings: 'GRAD' 150;
    background: var(--blue-dark);
  }
  .__grade__cta:focus {
    outline: 2px solid var(--red);
  }

  .__grade :is(.__grade__bold-on-hover, .__grade__better-bold-on-hover) {
    font-weight: 400;
  }

  .__grade__bold-on-hover:hover {
    font-weight: 800;
  }
  .__grade__better-bold-on-hover:hover {
    font-variation-settings: 'GRAD' 150;
  }
`;

function loadFont() {
  console.log('loading font');
  const font = new FontFace(
    'Robotoo',
    'url(/examples/grade-variable-fonts/roboto-subset-individual.woff2)'
  );

  // Add to the document.fonts (FontFaceSet)
  document.fonts.add(font);
}

const Component = ({ moveText, stillText, buttonText }) => {
  const [grade, setGrade] = useState('0');

  const exampleCSS = `p {
  font-family: Roboto;
  font-variation-settings: 'GRAD' ${grade};
}`;

  useEffect(() => {
    loadFont();
  }, []);

  return (
    <div className="__grade d-owl">
      <section
        aria-labelledby="grade-jumpy"
        className="__grade__example top-space"
      >
        Doggo ipsum tungg yapper{' '}
        <a href="#" className="__grade__bold-on-hover">
          he made many woofs I am bekom fat long doggo
        </a>{' '}
        such treat, blep very hand that feed shibe doggo.
        <div className="__grade__label top" id="grade-jumpy">
          <div>Bold hover styles leading to a heavy reflow.</div>
          <svg aria-hidden="true">
            <use xlinkHref="/sprite.svg#icon-arrow-right-top"></use>
          </svg>
        </div>
      </section>
      <p>{moveText}</p>
      <div>
        <div className="__grade__grid">
          <div>
            <CodeHighlight
              code={exampleCSS}
              language="css"
              style={{ fontSize: '0.875rem', marginTop: '0' }}
            ></CodeHighlight>
            <div className="__grade__controls">
              <label htmlFor="grade">Font grade</label>
              <input
                id="grade"
                type="range"
                min="-200"
                max="150"
                onChange={(e) => setGrade(e.target.value)}
                list="markers"
                step="50"
              />
              <datalist id="markers">
                <option value="-200"></option>
                <option value="-150"></option>
                <option value="-100"></option>
                <option value="-50"></option>
                <option value="0"></option>
                <option value="50"></option>
                <option value="100"></option>
                <option value="150"></option>
              </datalist>
            </div>
          </div>
          <div className="__grade__preview">
            <p
              className="__grade__paragraph"
              style={{ '--grade': grade, margin: '0' }}
            >
              Doggo ipsum tungg yapper he made many woofs I am bekom fat long
              doggo such treat, blep very hand that feed shibe doggo.
            </p>
          </div>
        </div>
      </div>
      <p>{stillText}</p>
      <section
        className="__grade__example bottom-space"
        aria-labelledby="grade-smooth"
      >
        <p className="__grade__fluent">
          Doggo ipsum tungg yapper{' '}
          <a href="#" className="__grade__better-bold-on-hover">
            he made many woofs I am bekom fat long doggo
          </a>{' '}
          such treat, blep very hand that feed shibe doggo.
        </p>
        <div className="__grade__label bottom" id="grade-smooth">
          <div>Bold styles done with relative font weight.</div>
          <svg aria-hidden="true">
            <use xlinkHref="/sprite.svg#icon-arrow-right-top"></use>
          </svg>
        </div>
      </section>
      <p>{buttonText}</p>
      <button type="button" className="__grade__cta">
        Hover me
      </button>
    </div>
  );
};

const renderComponent = (props, container) => {
  const root = createRoot(container);
  root.render(React.createElement(Component, props));
};

export { renderComponent, styles };
export default Component;
